import React, { useState, useEffect } from 'react';
import CodeRow from './CodeRow';
import './CodeRow.css';

// ==========================================================================
// 💡 折りたたみ対応のカテゴリセクションコンポーネント (一般画面用)
// ==========================================================================
const CategorySection = ({ category, today, showExpired }) => {
  const [isOpen, setIsOpen] = useState(true);

  const displayedItems = category.items.filter(item => {
    if (showExpired) return true;
    const itemDate = new Date(item.deadline);
    return itemDate >= today;
  });

  if (displayedItems.length === 0) return null;

  return (
    <section className="slim-section">
      <h2 className="slim-cat-title" onClick={() => setIsOpen(!isOpen)}>
        <span className={`accordion-arrow ${isOpen ? 'is-open' : ''}`}>▶</span>
        {category.title}
        <span className="item-count">({displayedItems.length})</span>
      </h2>
      
      {isOpen && (
        <div className="slim-list" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {displayedItems.map((item, itemIndex) => {
            const itemDate = new Date(item.deadline);
            const isExpired = itemDate < today;
            
            // 📅 「今月が期限（もうすぐ期限切れ）」かどうかの判定ロジック
            const isThisMonth = !isExpired && 
              itemDate.getFullYear() === today.getFullYear() && 
              itemDate.getMonth() === today.getMonth();
            
            // 🎨 今月内の場合、日付の見た目をリッチにする（オレンジ色＆太字）
            const displayDeadline = isThisMonth ? (
              <span style={{ color: '#e67e22', fontWeight: 'bold' }}>
                {item.deadline}
              </span>
            ) : item.deadline;
            
            return (
              <div 
                key={itemIndex} 
                style={isThisMonth ? { 
                  borderLeft: '4px solid #e67e22', // 左側にオレンジの線
                  background: '#fff9f2',          // 薄いオレンジの背景
                  borderRadius: '4px',
                  boxSizing: 'border-box'
                } : {}}
              >
                <CodeRow 
                  name={isThisMonth ? `⚠️ ${item.name}` : item.name} 
                  deadline={displayDeadline} // 👈 装飾した日付（または通常の日付）を渡す
                  code={item.code} 
                  isExpired={isExpired}
                />
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
};

// ==========================================================================
// 🛠️ 管理画面コンポーネント (名前のインライン編集対応版)
// ==========================================================================
const AdminScreen = ({ rawCategories, rawCards, onReload }) => {
  const [adminPassword, setAdminPassword] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [message, setMessage] = useState('');

  // フォーム用ステート
  const [newCatName, setNewCatName] = useState('');
  const [selectedCatId, setSelectedCatId] = useState('');
  const [newCardName, setNewCardName] = useState('');
  const [newCardDeadline, setNewCardDeadline] = useState('');
  const [newCardCode, setNewCardCode] = useState('');

  const handleLogin = (e) => {
    e.preventDefault();
    if (adminPassword === "shimaadmin1432") {
      setIsAuthorized(true);
      setMessage('');
    } else {
      alert('合言葉が違います！');
    }
  };

  const sendAdminRequest = async (action, type, data) => {
    try {
      const res = await fetch('/api/admin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: adminPassword, action, type, data })
      });
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || 'リクエストに失敗しました');
      
      setMessage(`✅ ${result.message}`);
      onReload();
      setTimeout(() => setMessage(''), 3000);
    } catch (err) {
      alert(`エラー: ${err.message}`);
    }
  };

  if (!isAuthorized) {
    return (
      <div className="site-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh', padding: '15px' }}>
        <form onSubmit={handleLogin} style={{ background: '#fff', padding: '25px 20px', borderRadius: '8px', boxShadow: '0 4px 10px rgba(0,0,0,0.1)', textAlign: 'center', maxWidth: '400px', width: '100%', boxSizing: 'border-box' }}>
          <h2 style={{ marginBottom: '20px', color: '#333', fontSize: '20px' }}>🔒 管理画面 ログイン</h2>
          <input 
            type="password" 
            placeholder="合言葉を入力" 
            value={adminPassword} 
            onChange={(e) => setAdminPassword(e.target.value)}
            style={{ width: '100%', padding: '12px', marginBottom: '15px', boxSizing: 'border-box', borderRadius: '6px', border: '1px solid #ccc', fontSize: '16px' }}
          />
          <button type="submit" style={{ width: '100%', padding: '12px', background: '#34495e', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' }}>ログイン</button>
        </form>
      </div>
    );
  }

  return (
    <div className="site-wrapper" style={{ fontFamily: 'sans-serif', padding: '10px', boxSizing: 'border-box' }}>
      
      <style>{`
        .admin-box { background: #fff; padding: 15px; borderRadius: '8px'; box-shadow: 0 4px 10px rgba(0,0,0,0.05); }
        .admin-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 20px; flex-wrap: wrap; gap: 10px; }
        .flex-container { display: flex; gap: 10px; flex-direction: row; }
        .card-form-grid { display: flex; flex-direction: column; gap: 10px; }
        .card-row-item { display: flex; justify-content: space-between; align-items: center; padding: 12px 8px; border-bottom: 1px dashed #eee; flex-wrap: wrap; gap: 10px; }
        .inline-edit-input { padding: 6px; borderRadius: 4px; border: 1px solid #ccc; fontSize: 14px; }
        
        @media (max-width: 600px) {
          .flex-container { flex-direction: column; }
          .flex-container button { width: 100%; padding: 12px; }
          .card-form-grid input, .card-form-grid select { width: 100%; box-sizing: border-box; padding: 12px !important; font-size: 16px !important; }
          .card-form-grid button { width: 100%; padding: 12px !important; }
          .card-row-item { flex-direction: column; align-items: flex-start; background: #fafafa; padding: 12px; border-radius: 6px; border: 1px solid #eee; }
          .card-row-item .inline-edit-input { width: 100% !important; box-sizing: border-box; padding: 8px !important; font-size: 15px !important; }
          .card-meta-edit { width: 100%; display: flex; flex-direction: column; gap: 6px; }
          .admin-header h1 { font-size: 18px !important; }
        }
      `}</style>

      <div className="site-container admin-box">
        
        <header className="admin-header">
          <h1 style={{ margin: 0, fontSize: '22px' }}>🛠️ パスワード管理</h1>
          <a href="/" style={{ fontSize: '14px', color: '#3498db', fontWeight: 'bold', background: '#eef7fc', padding: '6px 12px', borderRadius: '4px', textDecoration: 'none' }}>一般画面へ</a>
        </header>

        {message && <div style={{ background: '#e8f8f5', color: '#2ecc71', padding: '10px', borderRadius: '5px', marginBottom: '20px', fontWeight: 'bold', fontSize: '14px' }}>{message}</div>}

        {/* ーーー カテゴリの追加 ーーー */}
        <section style={{ background: '#f8f9fa', padding: '15px', borderRadius: '6px', marginBottom: '25px', borderLeft: '5px solid #2ecc71' }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '15px' }}>📂 カテゴリ（タイトル）の追加</h3>
          <div className="flex-container">
            <input 
              type="text" 
              placeholder="例: 名刺限定トレカ" 
              value={newCatName}
              onChange={(e) => setNewCatName(e.target.value)}
              style={{ flex: 1, padding: '10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '15px' }}
            />
            <button 
              onClick={() => {
                if(!newCatName) return;
                sendAdminRequest('add', 'category', { name: newCatName });
                setNewCatName('');
              }}
              style={{ padding: '10px 20px', background: '#2ecc71', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px' }}
            >
              追加
            </button>
          </div>
        </section>

        {/* ーーー カードの追加 ーーー */}
        <section style={{ background: '#f8f9fa', padding: '15px', borderRadius: '6px', marginBottom: '25px', borderLeft: '5px solid #3498db' }}>
          <h3 style={{ margin: '0 0 10px 0', fontSize: '15px' }}>🎴 カードの追加</h3>
          <div className="card-form-grid">
            <select 
              value={selectedCatId} 
              onChange={(e) => setSelectedCatId(e.target.value)}
              style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '15px', background: '#fff' }}
            >
              <option value="">▼ カテゴリを選択</option>
              {rawCategories.map(cat => <option key={cat.id} value={cat.id}>{cat.name}</option>)}
            </select>
            <input type="text" placeholder="カード名（例: 鈴木優）" value={newCardName} onChange={(e) => setNewCardName(e.target.value)} style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '15px' }} />
            <input type="date" placeholder="期限" value={newCardDeadline} onChange={(e) => setNewCardDeadline(e.target.value)} style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '15px' }} />
            <input type="text" placeholder="コード" value={newCardCode} onChange={(e) => setNewCardCode(e.target.value)} style={{ padding: '10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '15px' }} />
            
            <button 
              onClick={() => {
                if(!selectedCatId || !newCardName || !newCardCode) { alert('カテゴリ、カード名、コードは必須です'); return; }
                sendAdminRequest('add', 'card', { 
                  category_id: Number(selectedCatId), 
                  name: newCardName, 
                  expiry_date: newCardDeadline, 
                  code: newCardCode 
                });
                setNewCardName(''); setNewCardDeadline(''); setNewCardCode('');
              }}
              style={{ padding: '12px', background: '#3498db', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold', fontSize: '15px', marginTop: '5px' }}
            >
              カードを追加する
            </button>
          </div>
        </section>

        {/* ーーー 既存データの管理・編集・削除 ーーー */}
        <section>
          <h3 style={{ margin: '0 0 15px 0', borderBottom: '1px solid #ddd', paddingBottom: '5px', fontSize: '16px' }}>📝 既存データの編集 / 削除</h3>
          {rawCategories.map(cat => {
            const catCards = rawCards.filter(c => c.category_id === cat.id);
            return (
              <div key={cat.id} style={{ marginBottom: '20px', padding: '10px', border: '1px solid #e0e0e0', borderRadius: '6px' }}>
                
                {/* 📂 カテゴリ名の編集ヘッダー */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#f4f6f7', padding: '6px 10px', borderRadius: '4px', marginBottom: '10px', flexWrap: 'wrap', gap: '5px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', flex: 1 }}>
                    <span>📁</span>
                    <input 
                      type="text"
                      defaultValue={cat.name}
                      onBlur={(e) => {
                        if (e.target.value.trim() !== "" && e.target.value !== cat.name) {
                          sendAdminRequest('update', 'category', { id: cat.id, name: e.target.value });
                        }
                      }}
                      style={{ background: 'transparent', border: '1px dashed transparent', fontWeight: 'bold', fontSize: '14px', color: '#2c3e50', width: '80%', padding: '2px 4px' }}
                      onFocus={(e) => e.target.style.border = '1px dashed #999'}
                    />
                  </div>
                  <button 
                    onClick={() => { if(window.confirm(`カテゴリ「${cat.name}」と中のカードを全て削除しますか？`)) sendAdminRequest('delete', 'category', { id: cat.id }); }}
                    style={{ background: '#e74c3c', color: '#fff', border: 'none', padding: '4px 8px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px' }}
                  >
                    削除
                  </button>
                </div>

                {catCards.length === 0 ? (
                  <div style={{ padding: '5px 10px', color: '#999', fontSize: '13px' }}>カードがありません</div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {catCards.map(card => (
                      <div key={card.id} className="card-row-item">
                        
                        {/* 🎴 カード名のインライン編集 */}
                        <div className="card-meta-edit" style={{ flex: 1 }}>
                          <input 
                            type="text"
                            defaultValue={card.name}
                            onBlur={(e) => {
                              if (e.target.value.trim() !== "" && e.target.value !== card.name) {
                                sendAdminRequest('update', 'card', { ...card, name: e.target.value });
                              }
                            }}
                            className="inline-edit-input"
                            style={{ fontWeight: 'bold', color: '#333', border: '1px solid transparent', background: 'transparent', padding: '2px' }}
                            onFocus={(e) => e.target.style.border = '1px solid #ccc'}
                          />
                          <span style={{ color: '#e74c3c', fontSize: '12px', paddingLeft: '4px' }}>⏳ {card.expiry_date || 'なし'}</span>
                        </div>
                        
                        {/* パスワード（コード）のインライン編集 */}
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <span style={{ fontSize: '12px', color: '#666', whiteSpace: 'nowrap' }}>コード:</span>
                          <input 
                            type="text" 
                            defaultValue={card.code}
                            onBlur={(e) => {
                              if (e.target.value !== card.code) {
                                sendAdminRequest('update', 'card', { ...card, code: e.target.value });
                              }
                            }}
                            placeholder="コード"
                            className="inline-edit-input"
                            style={{ width: '120px' }}
                          />
                          <button 
                            onClick={() => { if(window.confirm(`「${card.name}」を削除しますか？`)) sendAdminRequest('delete', 'card', { id: card.id }); }}
                            style={{ background: 'none', color: '#e74c3c', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '0 5px' }}
                          >
                            ✕
                          </button>
                        </div>

                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </section>

      </div>
    </div>
  );
};

// ==========================================================================
// 🏠 メインコンポーネント (App)
// ==========================================================================
const App = () => {
  const [codeCategories, setCodeCategories] = useState([]);
  const [rawCategories, setRawCategories] = useState([]);
  const [rawCards, setRawCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const [showExpired, setShowExpired] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('admin') === 'true') {
      setIsAdmin(true);
    }
  }, []);

const fetchData = async () => {
    try {
      const res = await fetch('/api/data');
      if (!res.ok) throw new Error('データベースからのデータ取得に失敗しました');
      const fetchedData = await res.json();
      setCodeCategories(fetchedData);

      // 🔍 裏方から届いた本物のカードIDをそのまま管理用ステートに流し込む
      const cats = fetchedData.map(c => ({ id: c.id, name: c.title }));
      const crds = [];
      fetchedData.forEach(c => {
        c.items.forEach((item) => {
          crds.push({ 
            id: item.id, // 👈 本物のカードIDをマッピング
            category_id: c.id, 
            name: item.name, 
            expiry_date: item.deadline, 
            code: item.code 
          });
        });
      });
      setRawCategories(cats);
      setRawCards(crds);

      setLoading(false);
    } catch (err) {
      console.error(err);
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="site-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', color: '#666' }}>
        <div>⏳ 最新データを同期中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="site-wrapper" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh', color: '#e74c3c', flexDirection: 'column', gap: '10px' }}>
        <div>⚠️ データの読み込みに失敗しました</div>
        <button onClick={fetchData} style={{ padding: '5px 15px', cursor: 'pointer' }}>再試行</button>
      </div>
    );
  }

  if (isAdmin) {
    return <AdminScreen rawCategories={rawCategories} rawCards={rawCards} onReload={fetchData} />;
  }

  return (
    <div className="site-wrapper">
      <div className="site-container">
        
        <header className="site-header-slim">
          <h1 className="site-title-slim">Mリーグ限定トレカパスワード</h1>
          <label className="filter-label-slim">
            <input 
              type="checkbox" 
              checked={showExpired} 
              onChange={(e) => setShowExpired(e.target.checked)}
              className="filter-checkbox"
            />
            <span>期限切れも表示</span>
          </label>
        </header>
        
        {codeCategories.map((category, catIndex) => (
          <CategorySection 
            key={catIndex}
            category={category}
            today={today}
            showExpired={showExpired}
          />
        ))}

      </div>
    </div>
  );
};

export default App;
